import React, { useState, useEffect } from 'react';
import {
  listarEmailConnections,
  criarEmailConnection,
  atualizarEmailConnection,
  deletarEmailConnection,
  testarEmailConnection
} from '../../services/montagemPalete';

interface EmailConnectionData {
  id?: number;
  nome: string;
  smtp_host: string;
  smtp_port: number;
  smtp_user: string;
  smtp_pass: string;
  smtp_from: string;
  smtp_from_name: string;
  smtp_secure: boolean;
  smtp_tls: boolean;
  smtp_require_auth: boolean;
  ativo: boolean;
}

const defaultForm: EmailConnectionData = {
  nome: '',
  smtp_host: '',
  smtp_port: 587,
  smtp_user: '',
  smtp_pass: '',
  smtp_from: '',
  smtp_from_name: '',
  smtp_secure: false,
  smtp_tls: true,
  smtp_require_auth: true,
  ativo: true
};

const EmailConnections: React.FC = () => {
  const [connections, setConnections] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<EmailConnectionData>({ ...defaultForm });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const res = await listarEmailConnections();
      setConnections((res as any).dados || (res as any).data || []);
    } catch (err) {
      console.error('Erro ao carregar conexões email:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.nome || !formData.smtp_host || !formData.smtp_user || !formData.smtp_pass) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      if (editingId) {
        await atualizarEmailConnection(editingId, formData);
        setSuccess('Conexão atualizada com sucesso');
      } else {
        await criarEmailConnection(formData);
        setSuccess('Conexão criada com sucesso');
      }
      await loadConnections();
      resetForm();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar conexão');
    }
  };

  const handleEdit = (conn: any) => {
    setEditingId(conn.id);
    setFormData({
      nome: conn.nome,
      smtp_host: conn.smtp_host,
      smtp_port: conn.smtp_port,
      smtp_user: conn.smtp_user,
      smtp_pass: conn.smtp_pass || '',
      smtp_from: conn.smtp_from || '',
      smtp_from_name: conn.smtp_from_name || '',
      smtp_secure: conn.smtp_secure,
      smtp_tls: conn.smtp_tls,
      smtp_require_auth: conn.smtp_require_auth,
      ativo: conn.ativo
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Deseja realmente excluir esta conexão de email?')) return;
    try {
      await deletarEmailConnection(id);
      setSuccess('Conexão excluída com sucesso');
      await loadConnections();
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir conexão');
    }
  };

  const handleTest = async () => {
    setTestLoading(true);
    setError('');
    setSuccess('');
    try {
      if (editingId) {
        const res = await testarEmailConnection(editingId);
        if ((res as any).sucesso) {
          setSuccess('Conexão SMTP verificada com sucesso!');
        } else {
          setError((res as any).mensagem || 'Falha no teste de conexão');
        }
      } else {
        const res = await testarEmailConnection('test', formData);
        if ((res as any).sucesso) {
          setSuccess('Conexão SMTP verificada com sucesso!');
        } else {
          setError((res as any).mensagem || 'Falha no teste de conexão');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao testar conexão');
    } finally {
      setTestLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...defaultForm });
    setEditingId(null);
    setShowForm(false);
    setError('');
  };

  // Preset configs for common providers
  const applyPreset = (preset: string) => {
    switch (preset) {
      case 'gmail':
        setFormData(prev => ({ ...prev, smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_secure: false, smtp_tls: true }));
        break;
      case 'outlook':
        setFormData(prev => ({ ...prev, smtp_host: 'smtp.office365.com', smtp_port: 587, smtp_secure: false, smtp_tls: true }));
        break;
      case 'yahoo':
        setFormData(prev => ({ ...prev, smtp_host: 'smtp.mail.yahoo.com', smtp_port: 465, smtp_secure: true, smtp_tls: false }));
        break;
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#2c3e50' }}>Conexões de Email (SMTP)</h3>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#7f8c8d' }}>
            Configure servidores SMTP para envio automático de documentos por email
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            style={{
              padding: '8px 16px', background: '#04364F', color: '#fff',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '0.85rem', fontWeight: 600
            }}
          >
            + Nova Conexão Email
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div style={{ padding: '10px 14px', background: '#f8d7da', color: '#721c24', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ padding: '10px 14px', background: '#d4edda', color: '#155724', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.85rem' }}>
          {success}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div style={{ background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1.5rem', marginBottom: '1.5rem' }}>
          <h4 style={{ margin: '0 0 1rem', color: '#2c3e50' }}>
            {editingId ? 'Editar Conexão' : 'Nova Conexão de Email'}
          </h4>

          <form onSubmit={handleSubmit}>
            {/* Nome */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                Nome da Conexão *
              </label>
              <input
                type="text" value={formData.nome}
                onChange={e => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                placeholder="Ex: Gmail Empresa, Impressora e-Print"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
              />
            </div>

            {/* Presets */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                Provedor (atalho)
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['gmail', 'outlook', 'yahoo'].map(p => (
                  <button key={p} type="button" onClick={() => applyPreset(p)}
                    style={{ padding: '4px 12px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '0.8rem', textTransform: 'capitalize' }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* SMTP Settings Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                  Servidor SMTP *
                </label>
                <input
                  type="text" value={formData.smtp_host}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_host: e.target.value }))}
                  placeholder="smtp.gmail.com"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                  Porta *
                </label>
                <input
                  type="number" value={formData.smtp_port}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                  Usuário/Login *
                </label>
                <input
                  type="text" value={formData.smtp_user}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_user: e.target.value }))}
                  placeholder="usuario@email.com"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                  Senha *
                </label>
                <input
                  type="password" value={formData.smtp_pass}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_pass: e.target.value }))}
                  placeholder="••••••••"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                  Email Remetente
                </label>
                <input
                  type="email" value={formData.smtp_from}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_from: e.target.value }))}
                  placeholder="noreply@empresa.com (opcional)"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.85rem', color: '#555' }}>
                  Nome do Remetente
                </label>
                <input
                  type="text" value={formData.smtp_from_name}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                  placeholder="ConfereAí (opcional)"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.9rem' }}
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.smtp_require_auth}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_require_auth: e.target.checked }))} />
                Requer Autenticação
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.smtp_tls}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_tls: e.target.checked }))} />
                STARTTLS
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.smtp_secure}
                  onChange={e => setFormData(prev => ({ ...prev, smtp_secure: e.target.checked }))} />
                SSL/TLS (porta 465)
              </label>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={handleTest} disabled={testLoading}
                style={{
                  padding: '8px 16px', border: '1px solid #04364F', background: '#fff',
                  color: '#04364F', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                }}>
                {testLoading ? 'Testando...' : 'Testar Conexão'}
              </button>
              <button type="button" onClick={resetForm}
                style={{
                  padding: '8px 16px', border: '1px solid #ccc', background: '#fff',
                  color: '#666', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem'
                }}>
                Cancelar
              </button>
              <button type="submit"
                style={{
                  padding: '8px 16px', border: 'none', background: '#04364F',
                  color: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                }}>
                {editingId ? 'Atualizar' : 'Criar Conexão'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Connections List */}
      {loading ? (
        <p style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>Carregando...</p>
      ) : connections.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#7f8c8d' }}>
          <p style={{ fontSize: '1rem', fontWeight: 600 }}>Nenhuma conexão de email configurada</p>
          <p style={{ fontSize: '0.85rem' }}>Configure uma conexão SMTP para enviar documentos por email automaticamente</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }}>
          {connections.map((conn: any) => (
            <div key={conn.id} style={{
              border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: conn.ativo ? '#fff' : '#f5f5f5'
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '1.2rem' }}>📧</span>
                  <strong style={{ color: '#2c3e50' }}>{conn.nome}</strong>
                  <span style={{
                    padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 600,
                    background: conn.ativo ? '#d4edda' : '#f8d7da',
                    color: conn.ativo ? '#155724' : '#721c24'
                  }}>
                    {conn.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
                <div style={{ fontSize: '0.82rem', color: '#7f8c8d' }}>
                  {conn.smtp_host}:{conn.smtp_port} • {conn.smtp_user}
                  {conn.smtp_secure ? ' • SSL' : conn.smtp_tls ? ' • STARTTLS' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => handleEdit(conn)}
                  style={{ padding: '6px 12px', border: '1px solid #04364F', background: '#fff', color: '#04364F', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                  Editar
                </button>
                <button onClick={() => handleDelete(conn.id)}
                  style={{ padding: '6px 12px', border: '1px solid #e74c3c', background: '#fff', color: '#e74c3c', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailConnections;
